import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { Prompt, NotificationTarget } from '../../../../types/backend';
import PromptSelector from '../../../../shared/components/PromptSelector';
import ChannelFieldMappings from './ChannelFieldMappings';
import { EMAIL_CONTENT_KEYS } from '../../utils/ruleFormUtils';

interface NotificationTargetCardProps {
  target: NotificationTarget;
  onChange: (target: NotificationTarget) => void;
  onRemove: () => void;
  prompts: Prompt[];
  loadingPrompts: boolean;
  /** Whether rule-level enrichment is on. Off → format prompt can't run; field mappings are required. */
  summarize: boolean;
  hasAttemptedSave: boolean;
  recipientError?: string;
  /** Validation message per content key (subject, body). */
  mappingErrors: Partial<Record<string, string>>;
  sampleEvent: Record<string, unknown>;
  customerId: string;
  disabled?: boolean;
}

const parseRecipients = (text: string): string[] => text.split(/[\s,;]+/).filter(Boolean);

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

const NotificationTargetCard: React.FC<NotificationTargetCardProps> = ({
  target,
  onChange,
  onRemove,
  prompts,
  loadingPrompts,
  summarize,
  hasAttemptedSave,
  recipientError,
  mappingErrors,
  sampleEvent,
  customerId,
  disabled,
}) => {
  const isEmail = target.channel === 'email';

  // Kept as typed: rebuilding it from the parsed list would drop a separator the moment it is typed.
  const [recipientText, setRecipientText] = useState(target.to.join(', '));
  useEffect(() => {
    if (!sameList(parseRecipients(recipientText), target.to)) setRecipientText(target.to.join(', '));
  }, [target.to, recipientText]);

  const patch = (changes: Partial<NotificationTarget>) => {
    onChange({ ...target, ...changes });
  };

  return (
    <Box sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 1, pb: 0.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {isEmail ? 'Email' : 'SNS topic'}
        </Typography>
        <IconButton size="small" onClick={onRemove} disabled={disabled}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          fullWidth
          required
          size="small"
          label={isEmail ? 'Send to' : 'Topic ARN'}
          value={recipientText}
          onChange={(e) => {
            setRecipientText(e.target.value);
            patch({ to: parseRecipients(e.target.value) });
          }}
          error={hasAttemptedSave && !!recipientError}
          helperText={
            (hasAttemptedSave && recipientError) ||
            (isEmail
              ? 'Comma-separated'
              : 'Must allow sns:Publish from EventBridge')
          }
          placeholder={
            isEmail
              ? 'ops@example.com, alerts@example.com'
              : 'arn:aws:sns:eu-west-1:123456789012:alerts'
          }
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
        />

        {isEmail && (
          <>
            {/* A format prompt reshapes the enrichment — it can't run with enrichment off. */}
            {summarize && (
              <PromptSelector
                prompts={prompts}
                value={target.format_prompt_id ?? null}
                onChange={(id) => patch({ format_prompt_id: id ?? undefined })}
                loading={loadingPrompts}
                label="Reword (optional)"
                placeholder="Default formatting"
              />
            )}

            <Box>
              <Typography variant="caption" color={summarize ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', mb: 1 }}>
                {summarize
                  ? 'Field mapping: optional, a filled field overrides AI'
                  : 'Field mapping: required, map both fields from the event'}
              </Typography>
              <ChannelFieldMappings
                keys={EMAIL_CONTENT_KEYS}
                mappings={target.field_mappings || {}}
                onChange={(field_mappings) => patch({ field_mappings })}
                sampleEvent={sampleEvent}
                summarize={summarize}
                errors={hasAttemptedSave ? mappingErrors : {}}
                customerId={customerId}
                disabled={disabled}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NotificationTargetCard;
